import type { XmlNode } from '../types/xml-node'
import { type AnyRecord, asRecord, pick } from './tolerant-read'
import type { AttrSpec } from '../types/attr-spec'
import { formatValue } from './xml-attr-format'
export function node(
  tag: string,
  attrs: Array<[string, string]> = [],
  children: XmlNode[] = []
): XmlNode {
  return { tag, attrs, children }
}

/**
 * Escapes an attribute value the way Go's `encoding/xml` does, including numeric character
 * references for control characters - a newline-bearing text run appears as `T="&#x0A;"` in the
 * fixtures, so emitting a raw newline would not match a real file.
 */
export function escapeXmlAttr(value: string): string {
  let out = ''

  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0

    if (ch === '&') out += '&amp;'
    else if (ch === '<') out += '&lt;'
    else if (ch === '>') out += '&gt;'
    else if (ch === '"') out += '&quot;'
    else if (code < 0x20 || code === 0x7f) {
      out += `&#x${code.toString(16).toUpperCase().padStart(2, '0')};`
    } else out += ch
  }

  return out
}

export function escapeXmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function renderNode(target: XmlNode, depth: number): string[] {
  const pad = '\t'.repeat(depth)
  const lines: string[] = []

  if (target.comment) lines.push(`${pad}<!-- ${target.comment} -->`)
  if (target.tag === '') return lines

  const attrs = target.attrs.map(([k, v]) => ` ${k}="${escapeXmlAttr(v)}"`).join('')

  if (target.text !== undefined) {
    lines.push(`${pad}<${target.tag}${attrs}>${escapeXmlText(target.text)}</${target.tag}>`)

    return lines
  }

  if (target.children.length === 0) {
    lines.push(`${pad}<${target.tag}${attrs} />`)

    return lines
  }

  lines.push(`${pad}<${target.tag}${attrs}>`)
  for (const child of target.children) lines.push(...renderNode(child, depth + 1))
  lines.push(`${pad}</${target.tag}>`)

  return lines
}

export function buildAttrs(
  source: AnyRecord | null,
  specs: readonly AttrSpec[]
): Array<[string, string]> {
  const out: Array<[string, string]> = []

  for (const spec of specs) {
    const raw = pick(source, spec.from)
    const formatted = raw === undefined || raw === null ? spec.def : formatValue(raw, spec.kind)

    if (formatted === undefined) continue
    if (spec.omitWhen !== undefined && formatted === spec.omitWhen) continue

    out.push([spec.attr, formatted])
  }

  return out
}

/**
 * Last-resort emitter for a node type with no spec - sticky notes, `TOP`, ovals, a future kindId.
 * Uses the JSON key names verbatim and flags itself with a comment, so the output stays honest
 * about the fact that it is not a faithful transcript at that point.
 */
export function genericNode(tag: string, source: AnyRecord, comment?: string): XmlNode {
  const attrs: Array<[string, string]> = []
  const children: XmlNode[] = []

  for (const [key, value] of Object.entries(source)) {
    if (value === null || value === undefined) continue

    if (Array.isArray(value)) {
      for (const entry of value) {
        const rec = asRecord(entry)

        if (rec) children.push(genericNode(key, rec))
      }

      continue
    }

    const rec = asRecord(value)

    if (rec) {
      children.push(genericNode(key, rec))
      continue
    }

    attrs.push([key, String(value)])
  }

  const built = node(tag, attrs, children)

  if (comment) built.comment = comment

  return built
}
